import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  IndianRupee,
  Layers3,
  MapPin,
  MessageSquareText,
  Sparkles,
  Star,
  UserRound,
  Users,
  Wifi,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { dashboardDataService } from '../../services/dashboard-data.service';
import { createSessionsFromTeacherSelection } from '../../services/sessionScheduler';
import {
  getStudentScopedStorageItem,
  PENDING_TEACHER_SELECTION_STORAGE_KEY,
  SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
  SELECTED_TEACHERS_STORAGE_KEY,
  setStudentScopedStorageItem,
  removeStudentScopedStorageItem,
} from '../../utils/studentSelectionStorage';

interface Teacher {
  id: string;
  userId?: string;
  hasActiveSubscription?: boolean;
  name: string;
  subject: string;
  location: string;
  rating: number;
  rate: number;
  image: string;
  about: string;
  experience: string;
  availabilitySlots?: string[];
}

interface TeacherSelection {
  teacher: Teacher;
  preference: {
    subjectId?: string;
    subjectName: string;
    classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
    offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
    frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
    preferredTimeSlot?: string;
    startDate: string;
    endDate: string;
    notes: string;
  };
  selectedAt: string;
}

interface PendingTeacherSelectionPurchase {
  amount: number;
  selectionRequest: {
    teacherId: string;
    teacherName: string;
    subject: string;
    subjectId?: string;
    classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
    offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
    frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
    preferredTimeSlot?: string;
    startDate: string;
    endDate: string;
    notes?: string;
    subscriptionPlanId: string;
  };
  localSelection: TeacherSelection;
  nextSelections: TeacherSelection[];
  successMessage: string;
}

const classModeOptions: Array<{
  value: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: 'ONLINE', label: 'Online', description: 'Remote live classes', icon: Wifi },
  { value: 'OFFLINE', label: 'Offline', description: 'In-person sessions', icon: MapPin },
  { value: 'HYBRID', label: 'Hybrid', description: 'Mix of online and offline', icon: Layers3 },
];

const frequencyOptions: Array<{
  value: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
  label: string;
  description: string;
}> = [
  { value: 'DAILY', label: 'Daily', description: 'High-consistency learning rhythm' },
  { value: 'WEEKLY', label: 'Weekly', description: 'One focused class every week' },
  { value: 'BIWEEKLY', label: 'Bi-weekly', description: 'Two sessions per week' },
  { value: 'WEEKENDS', label: 'Weekends', description: 'Best for school-day schedules' },
];

const offlineSessionTypeOptions: Array<{
  value: 'HOME_SINGLE' | 'GROUP';
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: 'HOME_SINGLE', label: 'Home / Single', description: 'Personalized one-on-one format', icon: UserRound },
  { value: 'GROUP', label: 'Group', description: 'Shared learning environment', icon: Users },
];

const SelectTeacher: React.FC = () => {
  const { user } = useAuth();
  const offlineBenefitsUrl = 'https://example.com/offline-class-benefits';
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as { teacher?: Teacher; requestMode?: 'DEMO' | 'BOOKING' } | null;
  const teacher = routeState?.teacher;
  const isDemoRequest = routeState?.requestMode === 'DEMO';

  const [classMode, setClassMode] = useState<'ONLINE' | 'OFFLINE' | 'HYBRID'>('ONLINE');
  const [offlineSessionType, setOfflineSessionType] = useState<'HOME_SINGLE' | 'GROUP'>('HOME_SINGLE');
  const [frequency, setFrequency] = useState<'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS'>('WEEKLY');
  const [notes, setNotes] = useState(() =>
    isDemoRequest ? 'Demo class request: please confirm a suitable introductory session.' : '',
  );
  const [preferredTimeSlot, setPreferredTimeSlot] = useState('');
  const [preferredDay, setPreferredDay] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().slice(0, 10);
  });
  const [warning, setWarning] = useState<string | null>(null);
  const [showOfflinePopup, setShowOfflinePopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedTeacher = useMemo(() => teacher || null, [teacher]);
  const currentStudentUserId = String(user?.id || '').trim();
  const getTeacherKey = (value?: Teacher | null) => String(value?.userId || value?.id || '').trim();
  const selectedTeacherKey = String(selectedTeacher?.userId || selectedTeacher?.id || '').trim();
  const [availabilitySlots, setAvailabilitySlots] = useState<string[]>(
    () => selectedTeacher?.availabilitySlots || [],
  );

  useEffect(() => {
    setAvailabilitySlots(selectedTeacher?.availabilitySlots || []);
  }, [selectedTeacher]);

  useEffect(() => {
    if (!selectedTeacher || !selectedTeacherKey) return;

    let cancelled = false;

    const refreshAvailabilitySlots = async () => {
      try {
        const response = await api.get<Teacher[]>('/teachers/discover', {
          params: {
            subject: selectedTeacher.subject || undefined,
            classMode,
            offlineSessionType: classMode === 'OFFLINE' ? offlineSessionType : undefined,
            frequency,
          },
        });
        if (cancelled) return;

        const matchingTeacher = (response.data || []).find(
          (item) => String(item?.userId || item?.id || '').trim() === selectedTeacherKey,
        );
        setAvailabilitySlots(
          Array.isArray(matchingTeacher?.availabilitySlots)
            ? matchingTeacher?.availabilitySlots || []
            : [],
        );
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to refresh teacher availability:', error);
        setAvailabilitySlots(selectedTeacher.availabilitySlots || []);
      }
    };

    void refreshAvailabilitySlots();

    return () => {
      cancelled = true;
    };
  }, [
    classMode,
    frequency,
    offlineSessionType,
    selectedTeacher,
    selectedTeacherKey,
  ]);

  const parsedAvailability = useMemo(() => {
    const slots = availabilitySlots;
    const shortDayMap: Record<string, string> = {
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
      Sun: 'Sunday',
    };

    return slots
      .map((slot) => {
        if (slot.includes('|')) {
          const [day, time] = slot.split('|');
          return { day: day.trim(), time: time.trim() };
        }

        const [firstWord, ...rest] = slot.trim().split(' ');
        if (shortDayMap[firstWord] && rest.length > 0) {
          return { day: shortDayMap[firstWord], time: rest.join(' ').trim() };
        }

        return { day: 'UNSPECIFIED', time: slot.trim() };
      })
      .filter((slot) => slot.time);
  }, [availabilitySlots]);

  const dailyTimes = useMemo(
    () =>
      Array.from(
        new Set(
          parsedAvailability
            .filter((slot) => slot.day.toUpperCase() === 'DAILY' || slot.day === 'UNSPECIFIED')
            .map((slot) => slot.time),
        ),
      ),
    [parsedAvailability],
  );

  const weeklyDays = useMemo(
    () =>
      Array.from(
        new Set(
          parsedAvailability
            .filter((slot) => slot.day.toUpperCase() !== 'DAILY' && slot.day !== 'UNSPECIFIED')
            .map((slot) => slot.day),
        ),
      ),
    [parsedAvailability],
  );

  const timesForSelectedDay = useMemo(() => {
    if (!preferredDay) return [];
    return parsedAvailability.filter((slot) => slot.day === preferredDay).map((slot) => slot.time);
  }, [parsedAvailability, preferredDay]);

  useEffect(() => {
    if (frequency === 'DAILY') {
      if (preferredDay) {
        setPreferredDay('');
      }
      if (preferredTimeSlot && !dailyTimes.includes(preferredTimeSlot)) {
        setPreferredTimeSlot('');
      }
      return;
    }

    if (preferredDay && !weeklyDays.includes(preferredDay)) {
      setPreferredDay('');
      setPreferredTimeSlot('');
      return;
    }

    if (preferredTimeSlot && preferredDay && !timesForSelectedDay.includes(preferredTimeSlot)) {
      setPreferredTimeSlot('');
    }
  }, [
    dailyTimes,
    frequency,
    preferredDay,
    preferredTimeSlot,
    timesForSelectedDay,
    weeklyDays,
  ]);

  const formattedDateRange = useMemo(() => {
    if (!startDate || !endDate) return 'Pick your session window';
    const formatter = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${formatter.format(new Date(`${startDate}T00:00:00`))} - ${formatter.format(new Date(`${endDate}T00:00:00`))}`;
  }, [startDate, endDate]);

  const scheduleSummary = useMemo(() => {
    if (!preferredTimeSlot) return 'Schedule not finalized';
    if (frequency === 'DAILY') return `Daily at ${preferredTimeSlot}`;
    if (!preferredDay) return frequencyOptions.find((item) => item.value === frequency)?.label || 'Scheduled';
    return `${preferredDay} at ${preferredTimeSlot}`;
  }, [frequency, preferredDay, preferredTimeSlot]);

  const getStoredSelections = (): TeacherSelection[] => {
    const selectionsRaw = getStudentScopedStorageItem(
      SELECTED_TEACHERS_STORAGE_KEY,
      currentStudentUserId,
    );
    const singleSelectionRaw = getStudentScopedStorageItem(
      SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
      currentStudentUserId,
    );
    let selections: TeacherSelection[] = [];

    if (selectionsRaw) {
      try {
        const parsed = JSON.parse(selectionsRaw);
        if (Array.isArray(parsed)) selections = parsed;
      } catch (error) {
        console.warn('Failed to parse selectedTeachers from localStorage', error);
      }
    }

    if (selections.length === 0 && singleSelectionRaw) {
      try {
        const parsedSingle = JSON.parse(singleSelectionRaw);
        if (parsedSingle?.teacher?.id) selections = [parsedSingle];
      } catch (error) {
        console.warn('Failed to parse selectedTeacherPreference from localStorage', error);
      }
    }

    return selections;
  };

  const handleConfirmSelection = async () => {
    if (!selectedTeacher) return;
    setWarning(null);

    if (!currentStudentUserId) {
      setWarning('Student session was not found. Please log in again.');
      return;
    }

    if (selectedTeacher.hasActiveSubscription === false) {
      setWarning('Teacher profile is locked. Please subscribe to unlock and select this teacher.');
      return;
    }

    const existingSelections = getStoredSelections();
    const normalizedSubject = selectedTeacher.subject.trim().toLowerCase();
    const normalizedTeacherName = selectedTeacher.name.trim().toLowerCase();
    const sameSubjectSelection = existingSelections.find(
      (selection) => selection.teacher.subject?.trim().toLowerCase() === normalizedSubject,
    );
    if (!selectedTeacherKey) {
      setWarning('Teacher identifier is missing. Please select teacher again.');
      return;
    }

    const isSameTeacherById =
      !!sameSubjectSelection && getTeacherKey(sameSubjectSelection.teacher) === selectedTeacherKey;
    const isSameTeacherByName =
      !!sameSubjectSelection &&
      sameSubjectSelection.teacher.name?.trim().toLowerCase() === normalizedTeacherName;
    const isUpdatingSameTeacherSelection = !!sameSubjectSelection && (isSameTeacherById || isSameTeacherByName);

    if (sameSubjectSelection && !isUpdatingSameTeacherSelection) {
      setWarning(
        `You already selected ${sameSubjectSelection.teacher.name} for subject "${selectedTeacher.subject}". Please continue with that teacher.`,
      );
      return;
    }

    const payload: TeacherSelection = {
      teacher: selectedTeacher,
      preference: {
        subjectId: selectedTeacher.subject?.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        subjectName: selectedTeacher.subject,
        classMode,
        offlineSessionType: classMode === 'OFFLINE' ? offlineSessionType : undefined,
        frequency,
        preferredTimeSlot:
          frequency === 'DAILY'
            ? preferredTimeSlot || undefined
            : preferredDay && preferredTimeSlot
              ? `${preferredDay}|${preferredTimeSlot}`
              : undefined,
        startDate,
        endDate,
        notes: notes.trim(),
      },
      selectedAt: new Date().toISOString(),
    };

    if (!startDate || !endDate) {
      setWarning('Please select both start date and end date.');
      return;
    }

    const parsedStart = new Date(`${startDate}T00:00:00`);
    const parsedEnd = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
      setWarning('Invalid start date or end date.');
      return;
    }
    if (parsedStart.getTime() > parsedEnd.getTime()) {
      setWarning('Start date must be before or equal to end date.');
      return;
    }

    if (availabilitySlots.length > 0) {
      if (frequency === 'DAILY' && !preferredTimeSlot) {
        setWarning('Please select one available time slot.');
        return;
      }
      if (frequency !== 'DAILY' && (!preferredDay || !preferredTimeSlot)) {
        setWarning('Please select available day and time slot.');
        return;
      }
    }

    if (frequency === 'DAILY' && weeklyDays.length === 0 && dailyTimes.length === 0) {
      setWarning('Teacher has not provided daily availability slots.');
      return;
    }
    if (frequency !== 'DAILY' && weeklyDays.length === 0) {
      setWarning('Teacher has not provided weekly availability slots.');
      return;
    }
    if (!preferredTimeSlot) {
      setWarning('Preferred time slot is required.');
      return;
    }
    if (classMode === 'OFFLINE' && !offlineSessionType) {
      setWarning('Please select offline class type.');
      return;
    }

    const nextSelections = isUpdatingSameTeacherSelection
      ? existingSelections.map((selection) =>
          selection.teacher.subject?.trim().toLowerCase() === normalizedSubject &&
          (getTeacherKey(selection.teacher) === selectedTeacherKey ||
            selection.teacher.name?.trim().toLowerCase() === normalizedTeacherName)
            ? payload
            : selection,
        )
      : [...existingSelections, payload];

    const amount = Number(selectedTeacher.rate || 0);
    if (!Number.isFinite(amount) || amount < 0) {
      setWarning('Invalid teacher fee. Please choose another teacher or contact support.');
      return;
    }

    const subscriptionPlanId =
      `${selectedTeacherKey}:${payload.preference.subjectId || normalizedSubject}:${classMode}:${frequency}`.toLowerCase();

    const selectionRequest = {
      teacherId: selectedTeacherKey,
      teacherName: selectedTeacher.name,
      subject: selectedTeacher.subject,
      subjectId: selectedTeacher.subject?.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      classMode,
      offlineSessionType: classMode === 'OFFLINE' ? offlineSessionType : undefined,
      frequency,
      preferredTimeSlot:
        frequency === 'DAILY'
          ? preferredTimeSlot || undefined
          : preferredDay && preferredTimeSlot
            ? `${preferredDay}|${preferredTimeSlot}`
            : undefined,
      startDate: `${startDate}T00:00:00.000Z`,
      endDate: `${endDate}T00:00:00.000Z`,
      notes: notes.trim() || undefined,
      subscriptionPlanId,
    };

    const pendingPayload: PendingTeacherSelectionPurchase = {
      amount,
      selectionRequest,
      localSelection: payload,
      nextSelections,
      successMessage: isUpdatingSameTeacherSelection
        ? `${selectedTeacher.name} selection updated successfully.`
        : isDemoRequest
          ? `${selectedTeacher.name} demo request prepared successfully.`
          : `${selectedTeacher.name} selected successfully.`,
    };

    try {
      setIsSubmitting(true);
      if (isDemoRequest) {
        await api.post('/education-ops/demos', {
          status: 'REQUESTED',
          title: `Demo class with ${selectedTeacher.name}`,
          description: notes.trim() || `Demo request for ${selectedTeacher.subject}`,
          studentUserId: currentStudentUserId,
          teacherUserId: selectedTeacherKey,
          scheduledAt: startDate ? `${startDate}T00:00:00.000Z` : undefined,
          payload: {
            teacherName: selectedTeacher.name,
            subject: selectedTeacher.subject,
            classMode,
            offlineSessionType: classMode === 'OFFLINE' ? offlineSessionType : undefined,
            frequency,
            preferredTimeSlot:
              frequency === 'DAILY'
                ? preferredTimeSlot || undefined
                : preferredDay && preferredTimeSlot
                  ? `${preferredDay}|${preferredTimeSlot}`
                  : undefined,
            startDate,
            endDate,
            notes: notes.trim(),
            proposedSlots: availabilitySlots,
            demoFee: 0,
          },
        });
      }

      if (amount === 0) {
        await api.post('/teachers/select', selectionRequest);
        setStudentScopedStorageItem(
          SELECTED_TEACHERS_STORAGE_KEY,
          currentStudentUserId,
          JSON.stringify(nextSelections),
        );
        setStudentScopedStorageItem(
          SELECTED_TEACHER_PREFERENCE_STORAGE_KEY,
          currentStudentUserId,
          JSON.stringify(payload),
        );
        removeStudentScopedStorageItem(PENDING_TEACHER_SELECTION_STORAGE_KEY, currentStudentUserId);
        createSessionsFromTeacherSelection(payload);
        dashboardDataService.invalidateStudentSelections();
        alert(pendingPayload.successMessage);
        navigate('/student/dashboard');
        return;
      }

      setStudentScopedStorageItem(
        PENDING_TEACHER_SELECTION_STORAGE_KEY,
        currentStudentUserId,
        JSON.stringify(pendingPayload),
      );
      navigate('/student/wallet/topup', { state: { amount, role: 'STUDENT' } });
    } catch (error) {
      console.error('Failed to prepare teacher payment flow:', error);
      setWarning(
        amount === 0
          ? 'Could not accept this teacher selection. Please try again.'
          : 'Could not continue to payment. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClassModeChange = (mode: 'ONLINE' | 'OFFLINE' | 'HYBRID') => {
    setClassMode(mode);
    if (mode === 'OFFLINE') {
      setOfflineSessionType('HOME_SINGLE');
      setShowOfflinePopup(true);
    }
  };

  const handleFrequencyChange = (nextFrequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS') => {
    setFrequency(nextFrequency);
    setPreferredDay('');
    setPreferredTimeSlot('');
  };

  if (!selectedTeacher) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <div className="rounded-[1.75rem] border border-amber-200 bg-amber-50 p-6 text-amber-900 shadow-sm">
          Teacher profile context was not found. Please select a teacher again from Find Teachers.
        </div>
        <button
          type="button"
          onClick={() => navigate('/student/find-teachers')}
          className="mt-4 inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          Back To Find Teachers
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_32%),linear-gradient(180deg,_#f8fbff_0%,_#eef4f8_100%)]">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-white/70 bg-white/75 shadow-[0_30px_90px_-45px_rgba(15,23,42,0.45)] backdrop-blur">
          <div className="border-b border-slate-200/70 bg-[linear-gradient(135deg,_#0f172a_0%,_#1d4ed8_55%,_#38bdf8_100%)] px-6 py-8 text-white sm:px-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-100">Teacher Selection</p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Build the right class plan before you pay</h1>
                <p className="mt-3 max-w-xl text-sm leading-6 text-sky-50/90">
                  Review the teacher, lock your schedule, and send a cleaner request into the payment flow.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-[1.5rem] border border-white/20 bg-white/10 px-4 py-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-sky-100">Fee</p>
                  <div className="mt-2 flex items-center gap-2 text-lg font-semibold">
                    <IndianRupee className="h-5 w-5" />
                    <span>{selectedTeacher.rate}/hr</span>
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-white/20 bg-white/10 px-4 py-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-sky-100">Experience</p>
                  <p className="mt-2 text-lg font-semibold">{selectedTeacher.experience}</p>
                </div>
                <div className="rounded-[1.5rem] border border-white/20 bg-white/10 px-4 py-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-sky-100">Rating</p>
                  <div className="mt-2 flex items-center gap-2 text-lg font-semibold">
                    <Star className="h-5 w-5" />
                    <span>{selectedTeacher.rating}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-8 px-6 py-8 lg:grid-cols-[minmax(0,1.08fr)_360px] lg:px-8">
            <section className="space-y-6">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.5)] sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row">
                  <img
                    src={selectedTeacher.image}
                    alt={selectedTeacher.name}
                    className="h-24 w-24 rounded-[1.5rem] object-cover ring-4 ring-sky-100 sm:h-28 sm:w-28"
                  />
                  <div className="flex-1">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h2 className="text-2xl font-semibold tracking-tight text-slate-950">{selectedTeacher.name}</h2>
                        <p className="mt-1 text-sm font-medium text-sky-700">{selectedTeacher.subject}</p>
                      </div>
                      <div className="inline-flex items-center gap-2 self-start rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" />
                        Ready for booking
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5">
                        <MapPin className="h-4 w-4 text-slate-500" />
                        {selectedTeacher.location}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1.5 text-amber-700">
                        <Star className="h-4 w-4" />
                        {selectedTeacher.rating} rating
                      </span>
                    </div>
                    <p className="mt-4 text-sm leading-6 text-slate-600">{selectedTeacher.about}</p>
                  </div>
                </div>
              </div>

              {warning && (
                <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 shadow-sm">
                  {warning}
                </div>
              )}

              {isDemoRequest && (
                <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm leading-6 text-emerald-900 shadow-sm">
                  You are requesting a demo class. Pick a preferred slot and keep the note in place so the teacher can confirm the introductory session.
                </div>
              )}

              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.5)] sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-sky-50 p-3 text-sky-700">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950">Class setup</h3>
                    <p className="mt-1 text-sm text-slate-500">Choose how the classes should run and how often they should happen.</p>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-900">Class mode</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    {classModeOptions.map((option) => {
                      const Icon = option.icon;
                      const active = classMode === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleClassModeChange(option.value)}
                          className={`rounded-[1.5rem] border p-4 text-left transition ${
                            active
                              ? 'border-sky-500 bg-sky-50 shadow-[0_18px_36px_-28px_rgba(14,165,233,0.7)]'
                              : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <Icon className={`h-5 w-5 ${active ? 'text-sky-700' : 'text-slate-500'}`} />
                            {active && <CheckCircle2 className="h-5 w-5 text-sky-700" />}
                          </div>
                          <p className="mt-6 text-base font-semibold text-slate-950">{option.label}</p>
                          <p className="mt-1 text-sm text-slate-500">{option.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {classMode === 'OFFLINE' && (
                  <div className="mt-6">
                    <p className="text-sm font-semibold text-slate-900">Offline class type</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {offlineSessionTypeOptions.map((option) => {
                        const Icon = option.icon;
                        const active = offlineSessionType === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setOfflineSessionType(option.value)}
                            className={`rounded-[1.5rem] border p-4 text-left transition ${
                              active
                                ? 'border-emerald-500 bg-emerald-50 shadow-[0_18px_36px_-28px_rgba(16,185,129,0.7)]'
                                : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <Icon className={`h-5 w-5 ${active ? 'text-emerald-700' : 'text-slate-500'}`} />
                              {active && <CheckCircle2 className="h-5 w-5 text-emerald-700" />}
                            </div>
                            <p className="mt-6 text-base font-semibold text-slate-950">{option.label}</p>
                            <p className="mt-1 text-sm text-slate-500">{option.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-900">Frequency</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {frequencyOptions.map((option) => {
                      const active = frequency === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleFrequencyChange(option.value)}
                          className={`rounded-[1.5rem] border p-4 text-left transition ${
                            active
                              ? 'border-violet-500 bg-violet-50 shadow-[0_18px_36px_-28px_rgba(139,92,246,0.65)]'
                              : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <CalendarDays className={`h-5 w-5 ${active ? 'text-violet-700' : 'text-slate-500'}`} />
                            {active && <CheckCircle2 className="h-5 w-5 text-violet-700" />}
                          </div>
                          <p className="mt-6 text-base font-semibold text-slate-950">{option.label}</p>
                          <p className="mt-1 text-sm text-slate-500">{option.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.5)] sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-amber-50 p-3 text-amber-700">
                    <Clock3 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950">Availability and dates</h3>
                    <p className="mt-1 text-sm text-slate-500">Match your preferred timing to the teacher&apos;s schedule.</p>
                  </div>
                </div>

                <div className="mt-6 space-y-5">
                  {availabilitySlots.length > 0 ? (
                    frequency === 'DAILY' ? (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {dailyTimes.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setPreferredTimeSlot(slot)}
                            className={`rounded-[1.25rem] border px-4 py-3 text-left text-sm transition ${
                              preferredTimeSlot === slot
                                ? 'border-slate-950 bg-slate-950 text-white'
                                : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
                        <div className="grid gap-2">
                          {weeklyDays.map((day) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                setPreferredDay(day);
                                setPreferredTimeSlot('');
                              }}
                              className={`rounded-[1.1rem] border px-4 py-3 text-left text-sm transition ${
                                preferredDay === day
                                  ? 'border-sky-500 bg-sky-50 text-sky-900'
                                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {timesForSelectedDay.length > 0 ? (
                            timesForSelectedDay.map((slot) => (
                              <button
                                key={slot}
                                type="button"
                                onClick={() => setPreferredTimeSlot(slot)}
                                className={`rounded-[1.25rem] border px-4 py-3 text-left text-sm transition ${
                                  preferredTimeSlot === slot
                                    ? 'border-slate-950 bg-slate-950 text-white'
                                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                                }`}
                              >
                                {slot}
                              </button>
                            ))
                          ) : (
                            <div className="rounded-[1.25rem] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                              Select a day first to see available timings.
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  ) : (
                    <input
                      type="text"
                      value={preferredTimeSlot}
                      onChange={(e) => setPreferredTimeSlot(e.target.value)}
                      className="w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                      placeholder={frequency === 'DAILY' ? '10:00 AM - 11:00 AM' : 'Monday|10:00 AM - 11:00 AM'}
                    />
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.5)] sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">
                    <MessageSquareText className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950">Notes for the teacher</h3>
                    <p className="mt-1 text-sm text-slate-500">Share goals, exam priorities, or any timing constraints.</p>
                  </div>
                </div>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-5 w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  placeholder="Preferred timing, syllabus focus, exam target, learning goals..."
                />
              </div>
            </section>

            <aside className="space-y-5">
              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-[0_25px_70px_-40px_rgba(15,23,42,0.8)]">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-300">Booking Summary</p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight">{selectedTeacher.name}</h3>
                <p className="mt-1 text-sm text-slate-300">{selectedTeacher.subject}</p>
                <div className="mt-6 space-y-3">
                  <div className="rounded-[1.25rem] bg-white/5 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Class mode</p>
                    <p className="mt-1 text-sm font-medium text-white">
                      {classModeOptions.find((item) => item.value === classMode)?.label}
                    </p>
                  </div>
                  <div className="rounded-[1.25rem] bg-white/5 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Schedule</p>
                    <p className="mt-1 text-sm font-medium text-white">{scheduleSummary}</p>
                  </div>
                  <div className="rounded-[1.25rem] bg-white/5 px-4 py-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Duration window</p>
                    <p className="mt-1 text-sm font-medium text-white">{formattedDateRange}</p>
                  </div>
                  {classMode === 'OFFLINE' && (
                    <div className="rounded-[1.25rem] bg-white/5 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Offline format</p>
                      <p className="mt-1 text-sm font-medium text-white">
                        {offlineSessionTypeOptions.find((item) => item.value === offlineSessionType)?.label}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    {isDemoRequest
                      ? 'Demo request'
                      : Number(selectedTeacher.rate || 0) === 0
                        ? 'Selection fee'
                        : 'Payable now'}
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-3xl font-semibold">
                    {Number(selectedTeacher.rate || 0) === 0 ? (
                      <span>{isDemoRequest ? 'Slot' : 'Free'}</span>
                    ) : (
                      <>
                        <IndianRupee className="h-7 w-7" />
                        <span>{selectedTeacher.rate}</span>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-slate-300">
                    {Number(selectedTeacher.rate || 0) === 0
                      ? isDemoRequest
                        ? 'The request keeps your preferred demo timing and teacher note together.'
                        : 'This teacher can be selected immediately without wallet top-up.'
                      : isDemoRequest
                        ? 'This continues through the existing booking flow while preserving the demo request note.'
                        : 'This amount is used to initiate the wallet top-up and booking flow.'}
                  </p>
                </div>
                <div className="mt-6 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={handleConfirmSelection}
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting
                      ? Number(selectedTeacher.rate || 0) === 0
                        ? 'Submitting...'
                        : 'Redirecting...'
                      : Number(selectedTeacher.rate || 0) === 0
                        ? isDemoRequest
                          ? 'Send Demo Request'
                          : 'Accept Selection'
                        : isDemoRequest
                          ? 'Continue Demo Request'
                          : 'Proceed To Payment'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/student/find-teachers')}
                    className="inline-flex items-center justify-center rounded-full border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-white/10"
                  >
                    Back To Teacher List
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {showOfflinePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4">
          <div className="w-full max-w-md rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-45px_rgba(15,23,42,0.55)]">
            <h4 className="text-lg font-semibold text-slate-950">Offline Class Benefit</h4>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Offline classes can unlock additional learning benefits. Review the details here:
            </p>
            <a
              href={offlineBenefitsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 block break-all text-sm font-medium text-sky-700 underline"
            >
              {offlineBenefitsUrl}
            </a>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowOfflinePopup(false)}
                className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectTeacher;
